import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * useGeolocation
 * Handles browser Geolocation permission flow, watchPosition, and throttled
 * push of coordinates to the backend. Also calls `onFix` after each successful
 * push so callers can refresh nearby data.
 *
 * States: idle | prompting | granted | denied | unsupported | error
 */
export function useGeolocation({ onFix } = {}) {
  const [geoState, setGeoState] = useState("idle");
  const [geoError, setGeoError] = useState("");
  const [coords, setCoords] = useState(null);
  const watchIdRef = useRef(null);
  const lastPushRef = useRef({ lat: null, lng: null, at: 0 });
  const onFixRef = useRef(onFix);
  useEffect(() => { onFixRef.current = onFix; }, [onFix]);

  const pushLocation = async (lat, lng) => {
    // Throttle: skip if we pushed the same coord within 8s or moved <10m
    const now = Date.now();
    const prev = lastPushRef.current;
    const dt = now - prev.at;
    if (prev.lat != null && dt < 8000) {
      const dLat = Math.abs(lat - prev.lat);
      const dLng = Math.abs(lng - prev.lng);
      if (dLat < 0.0001 && dLng < 0.0001) return;
    }
    lastPushRef.current = { lat, lng, at: now };
    try {
      await api.post("/profile/location", { lat, lng });
      await onFixRef.current?.();
    } catch (err) {
      console.error("push location failed", err);
    }
  };

  const enable = () => {
    if (!("geolocation" in navigator)) {
      setGeoState("unsupported");
      setGeoError("This browser does not expose GPS. Try Chrome, Safari, or Firefox on a device with location.");
      return;
    }
    setGeoState("prompting");
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setCoords({ lat: latitude, lng: longitude, accuracy });
        setGeoState("granted");
        await pushLocation(latitude, longitude);
      },
      (err) => {
        if (err.code === 1) {
          setGeoState("denied");
          setGeoError("Location permission was denied. Enable it in your browser settings to appear on the radar.");
        } else {
          setGeoState("error");
          setGeoError(err.message || "Could not read your location.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );

    if (watchIdRef.current == null) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          setCoords({ lat: latitude, lng: longitude, accuracy });
          setGeoState("granted");
          pushLocation(latitude, longitude);
        },
        (err) => {
          if (err.code === 1) {
            setGeoState("denied");
            setGeoError("Location permission was denied.");
          }
        },
        { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 },
      );
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: "geolocation" });
          if (cancelled) return;
          if (status.state === "granted") enable();
          else if (status.state === "denied") {
            setGeoState("denied");
            setGeoError("Location permission was denied. Enable it in your browser to appear on the radar.");
          } else {
            setGeoState("idle");
          }
          status.onchange = () => {
            if (status.state === "granted") enable();
            if (status.state === "denied") { setGeoState("denied"); setGeoError("Location permission was denied."); }
          };
        } else {
          enable();
        }
      } catch {
        setGeoState("idle");
      }
    })();
    return () => {
      cancelled = true;
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  return { geoState, geoError, coords, enable };
}
